import { useId, useState } from 'react'
import { ApiClientError } from '../api/types.js'
import { Alert } from '../ui/Alert.js'
import { CopyButton } from '../ui/CopyButton.js'
import { вКавычках } from '../ui/quotes.js'

/**
 * Первичная настройка установки (FR-INST-014).
 *
 * До неё портал был тупиком: пользователей нет, войти некому, а завести
 * первого можно было только командой в консоли сервера. Тот, кто
 * разворачивает установку, видел форму входа, в которую нечего ввести.
 *
 * Три шага и ни одним больше: кто вы, как называется организация, чем
 * подключать 1С. Всё остальное — номера, сценарии, права — делается
 * потом и уже внутри портала: настройка не должна спрашивать того, что
 * человек ещё не решил.
 */
export interface SetupClient {
  setupRequired(): Promise<boolean>
  completeSetup(input: {
    email: string
    password: string
    organization: string
    apiKeyName: string
  }): Promise<{ tenantId: string; apiKey: string; apiKeyPrefix: string }>
}

export interface SetupProps {
  client: SetupClient
  /** Настройка закончена: оболочка показывает вход с уже введённой почтой. */
  onDone: (email: string) => void
}

const GENERIC_FAILURE = 'Не удалось завершить настройку. Проверьте соединение и попробуйте ещё раз.'

export function Setup({ client, onDone }: SetupProps): React.JSX.Element {
  const emailId = useId()
  const passwordId = useId()
  const organizationId = useId()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [organization, setOrganization] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ tenantId: string; apiKey: string } | null>(null)

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setPending(true)
    setError(null)
    try {
      const created = await client.completeSetup({
        email,
        password,
        organization,
        apiKeyName: 'Интеграция с 1С',
      })
      setDone({ tenantId: created.tenantId, apiKey: created.apiKey })
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    } finally {
      setPending(false)
    }
  }

  if (done !== null) {
    return (
      <div className="вход">
        <main className="shell shell--шире">
          <div className="вход__метки" aria-hidden="true">
            <span className="вход__метка" />
            <span className="вход__метка" />
            <span className="вход__метка" />
          </div>
          <h1>Установка настроена</h1>
          <p className="shell__hint">
            Учётная запись создана, организация {вКавычках(organization)} заведена. Осталось сохранить
            ключ — и можно подключать номер.
          </p>

          <div className="card">
            <h2 className="ключ__заголовок">Ключ для интеграции</h2>
            <p className="ключ__зачем">
              Им внешние системы — 1С, сайт, CRM — обращаются к платформе. Передаётся заголовком{' '}
              <code>X-API-Key</code>.
            </p>
            {/* Ключ показывается ровно один раз: в базе лежит его хэш, и
                восстановить значение нельзя ни через портал, ни через API.
                Сказать об этом надо до того, как человек закроет вкладку. */}
            <p className="ключ__значение моно">{done.apiKey}</p>
            <p className="ключ__предупреждение">
              Скопируйте его сейчас. Второй раз ключ не покажется: платформа хранит только его
              отпечаток. Потерялся — выпустите новый в разделе «Доступ в портал».
            </p>
            {/* Кнопка стоит рядом с требованием скопировать (FR-ADM-036):
                до неё экран просил сделать то, чего сам не давал сделать. */}
            <CopyButton value={done.apiKey} what="Ключ" label="Скопировать ключ" />
          </div>

          <button type="button" className="btn btn--primary" onClick={() => onDone(email)}>
            Войти в портал
          </button>
        </main>
      </div>
    )
  }

  return (
    <div className="вход">
      <main className="shell shell--шире">
        <div className="вход__метки" aria-hidden="true">
          <span className="вход__метка" />
          <span className="вход__метка" />
          <span className="вход__метка" />
        </div>
        <h1>Настройка платформы</h1>
        <p className="shell__hint">
          Установка новая: в ней ещё нет ни одной учётной записи. Заведите свою — и платформа
          будет готова к работе.
        </p>

        <form className="card" onSubmit={(event) => void submit(event)}>
          <div className="field">
            <label htmlFor={organizationId}>Название организации</label>
            <input
              id={organizationId}
              value={organization}
              onChange={(event) => setOrganization(event.target.value)}
              placeholder="ООО «Ромашка»"
              required
            />
            <p className="поле__подсказка">
              Так платформа назовёт ваше рабочее пространство. Номера, сценарии и ключи будут
              принадлежать ему.
            </p>
          </div>

          <div className="field">
            <label htmlFor={emailId}>Ваша почта</label>
            <input
              id={emailId}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <p className="поле__подсказка">Ею вы будете входить в портал.</p>
          </div>

          <div className="field">
            <label htmlFor={passwordId}>Пароль</label>
            <input
              id={passwordId}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={8}
              required
            />
            <p className="поле__подсказка">
              От восьми символов. Короче — подбирается за вечер.
            </p>
          </div>

          {error !== null && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

          <button type="submit" className="btn btn--primary" disabled={pending}>
            {pending ? 'Настраиваем…' : 'Настроить платформу'}
          </button>
        </form>
      </main>
    </div>
  )
}
